/* eslint-disable quotes */
import React, { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { Form } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { AppLanguage } from 'app/config/i18Config/types';
import { showNotification } from 'app/providers/NotificationsProvider';
import { LeadData, LeadFirstContact, useCreateLeadMutation } from 'entities/Lead';
import { getSizeCodeTypeString, SizeCodeType } from 'entities/SizeCode';
import { getLoggedUserData } from 'entities/User';
import { GratitudeModalType, useOpenGratitudeModal } from 'features/GratitudeModal';
import {
  getFacebookClickIdFromCookie,
  getGoogleClickIdDateFromCookie,
  getGoogleClickIdFromCookie,
  getUtmTrackingParamsFromCookie,
} from 'features/TrackingParams';
import { getGlobalSettings } from 'app/appState';
import { getBoxInfo, getWarehouseInfo } from 'pages/BookingPage';
import { Button } from 'shared/ui/Button';
import { DatePicker } from 'shared/ui/DatePicker';
import { Input } from 'shared/ui/Input';
import { PhoneInput } from 'shared/ui/PhoneInput';
import { Select } from 'shared/ui/Select';
import { ReactComponent as LocationIcon } from 'shared/assets/icons/LocationIcon.svg';
import { CLIENT_DATE_FORMAT, APP_LANGUAGE_KEY } from 'shared/utils/constants';
import { getServerDateWithMinuteTimeFormat } from 'shared/utils/helpers/getDateFormat';
import { PHONE_NUMBER_REGEX } from 'shared/utils/regex';
import { BOOK_TOUR_PERIODS } from '../model/constants/bookTourPeriods';
import { getModalState } from '../model/selectors/getModalState';

const POLAND_CLIENT_NAME = 'rentabox24pl';

const BOOK_TOUR_GRATITUDE_DESCRIPTION =
  'We have received your viewing request.\nWe will contact you by email or phone on the next business day to confirm the appointment.\n\nSee you soon!';

interface BookTourFormProps {
  onClose: () => void;
}

interface BookTourFormValues {
  bookTourDate: Dayjs;
  bookTourPeriod: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  sizeCodeId?: string;
}

export const BookTourForm: FC<BookTourFormProps> = memo((props) => {
  const { onClose } = props;

  const { t } = useAppTranslation(['booking', 'common']);
  const [form] = Form.useForm<BookTourFormValues>();
  const [createLead, { isLoading }] = useCreateLeadMutation();
  const { openGratitudeModal } = useOpenGratitudeModal();

  const modalState = useAppSelector(getModalState);
  const loggedUserData = useAppSelector(getLoggedUserData);
  const warehouse = useAppSelector(getWarehouseInfo);
  const boxData = useAppSelector(getBoxInfo);
  const globalSettings = useAppSelector(getGlobalSettings);
  const isPolandClient = globalSettings?.clientName === POLAND_CLIENT_NAME;
  const contactEmail = globalSettings?.contactEmail;
  const contactPhone = globalSettings?.contactPhone?.join(', ');

  const locationLabel = useMemo(() => {
    if (!warehouse) {
      return '';
    }

    return [warehouse.address, warehouse.zip, warehouse.cityName].filter(Boolean).join(', ');
  }, [warehouse]);

  const sizeCodeOptions = useMemo(
    () =>
      (warehouse?.sizeCodes || [])
        .filter((sizeCode) => sizeCode.sizeCodeType !== SizeCodeType.OFFICE)
        .filter((sizeCode) => !isPolandClient || sizeCode.availableBoxesCount > 0)
        .map((sizeCode) => ({
          value: sizeCode.sizeCodeId,
          label: `${sizeCode.square} ${getSizeCodeTypeString(sizeCode.sizeCodeType)}`,
        })),
    [isPolandClient, warehouse?.sizeCodes],
  );

  const bookTourPeriodOptions = useMemo(
    () =>
      BOOK_TOUR_PERIODS.map((period) => ({
        value: period,
        label: period,
      })),
    [],
  );

  useEffect(() => {
    const preferredSizeCodeId = modalState.sizeCodeId || boxData?.sizeCode?.sizeCodeId;
    const isPreferredSizeAvailable = sizeCodeOptions.some((option) => option.value === preferredSizeCodeId);

    form.setFieldsValue({
      firstName: loggedUserData?.firstName || undefined,
      lastName: loggedUserData?.lastName || undefined,
      email: loggedUserData?.email || undefined,
      phone: loggedUserData?.phone || undefined,
      sizeCodeId: isPreferredSizeAvailable ? preferredSizeCodeId : undefined,
    });
  }, [boxData?.sizeCode?.sizeCodeId, form, loggedUserData, modalState.sizeCodeId, sizeCodeOptions]);

  const disableUnavailableDates = useCallback(
    (currentDate: Dayjs): boolean => {
      const isTodayOrPast = !currentDate.startOf('day').isAfter(dayjs().startOf('day'));

      if (!isPolandClient) {
        return isTodayOrPast;
      }

      const isWeekend = currentDate.day() === 0 || currentDate.day() === 6;
      const isNextDay = currentDate.isSame(dayjs().add(1, 'day'), 'day');

      return isTodayOrPast || isWeekend || isNextDay;
    },
    [isPolandClient],
  );

  const handleSubmit = useCallback(
    async (data: BookTourFormValues): Promise<void> => {
      try {
        const language = localStorage.getItem(APP_LANGUAGE_KEY) as AppLanguage;
        const utmParams = getUtmTrackingParamsFromCookie();
        const selectedSizeCode = warehouse?.sizeCodes?.find((sizeCode) => sizeCode.sizeCodeId === data.sizeCodeId);
        const sizeLabel = selectedSizeCode
          ? `${selectedSizeCode.square} ${getSizeCodeTypeString(selectedSizeCode.sizeCodeType)}`
          : undefined;
        const [periodStart] = data.bookTourPeriod.split(' - ');
        const [hours, minutes] = periodStart.split(':').map(Number);
        const tourDate = getServerDateWithMinuteTimeFormat(data.bookTourDate.hour(hours).minute(minutes).second(0).millisecond(0));

        const noteParts: string[] = [`Tour date: ${data.bookTourDate.format(CLIENT_DATE_FORMAT)}`, `Period: ${data.bookTourPeriod}`];

        if (warehouse?.name) {
          noteParts.push(`Warehouse: ${warehouse.name}`);
        }

        if (modalState.boxName) {
          noteParts.push(`Box: ${modalState.boxName}`);
        }

        if (sizeLabel) {
          noteParts.push(`Size: ${sizeLabel}`);
        }

        const leadData: LeadData = {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          language,
          warehouseId: warehouse?.warehouseId,
          square: selectedSizeCode?.square || modalState.square || boxData?.sizeCode?.square,
          firstContact: LeadFirstContact.CONTACT_FORM,
          tourDate,
          note: noteParts.length ? noteParts.join('\n') : undefined,
          googleClickId: getGoogleClickIdFromCookie(),
          googleClickIdDate: getGoogleClickIdDateFromCookie(),
          fbClickId: getFacebookClickIdFromCookie(),
          ...utmParams,
        };

        await createLead(leadData).unwrap();

        onClose();
        showNotification('info', t('Success', { ns: 'common' }), t('Your request has been successfully sent', { ns: 'common' }));
        openGratitudeModal({
          description: t(BOOK_TOUR_GRATITUDE_DESCRIPTION),
          type: GratitudeModalType.CONTACT_FORM,
          buttonId: 'book tour id',
        });
      } catch {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when sending request', { ns: 'common' }));
      }
    },
    [
      boxData?.sizeCode?.square,
      createLead,
      modalState.boxName,
      modalState.square,
      onClose,
      openGratitudeModal,
      t,
      warehouse?.name,
      warehouse?.sizeCodes,
      warehouse?.warehouseId,
    ],
  );

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit} className="flex flex-col gap-4">
      <div className="text-2xl font-semibold">{t('Visit location')}</div>
      {locationLabel && (
        <div className="inline-flex w-fit max-w-full items-center gap-2 rounded-full bg-secondaryLight px-3 py-1.5 text-sm text-primaryLight">
          <LocationIcon className="shrink-0 stroke-primaryLight" />
          <span className="truncate">{locationLabel}</span>
        </div>
      )}
      <div className="text-primaryLight whitespace-pre-line">
        {t(
          "See the storage units in person and choose the size that suits you best.\nAfter you submit the request, we'll contact you on the next business day to confirm the visit.\n\nIf the right unit isn't available, email us at {{email}} or call {{phone}}.",
          {
            email: contactEmail,
            phone: contactPhone,
          },
        )}
      </div>
      <div className="flex flex-col gap-3 desktop:flex-row">
        <Form.Item name="bookTourDate" className="mb-0 w-full" rules={[{ required: true, message: t('Please, select a day') }]}>
          <DatePicker
            format={CLIENT_DATE_FORMAT}
            placeholder={t('Select day')}
            bordered
            allowClear={false}
            showToday={false}
            disabledDate={disableUnavailableDates}
            className="h-14 w-full"
          />
        </Form.Item>
        <Form.Item name="bookTourPeriod" className="mb-0 w-full" rules={[{ required: true, message: t('Please, select a period') }]}>
          <Select
            options={bookTourPeriodOptions}
            placeholder={t('Select period')}
            bordered
            containerClassName="w-full"
            className="w-full"
          />
        </Form.Item>
      </div>
      <div className="flex flex-col gap-3 desktop:flex-row">
        <Form.Item name="firstName" className="mb-0 w-full" rules={[{ required: true, message: t('Please, enter your name') }]}>
          <Input placeholder={t('First name')} bordered disabled={Boolean(loggedUserData?.firstName)} containerClassName="w-full" />
        </Form.Item>
        <Form.Item name="lastName" className="mb-0 w-full" rules={[{ required: true, message: t('Please, enter your last name') }]}>
          <Input placeholder={t('Last name')} bordered disabled={Boolean(loggedUserData?.lastName)} containerClassName="w-full" />
        </Form.Item>
      </div>
      <Form.Item
        name="email"
        className="mb-0 w-full"
        rules={[
          { required: true, message: t('Please, enter your email') },
          { type: 'email', message: t('Please enter correct email!') },
        ]}
      >
        <Input placeholder={t('E-mail address')} bordered disabled={Boolean(loggedUserData?.email)} containerClassName="w-full" />
      </Form.Item>
      <Form.Item
        name="phone"
        className="mb-0 w-full"
        rules={[
          { pattern: PHONE_NUMBER_REGEX, message: t('Please provide a valid phone number!') },
          { required: true, message: t('Please, enter your phone') },
        ]}
      >
        <PhoneInput placeholder={t('Mobile number')} bordered initialValue={loggedUserData?.phone} className="w-full" />
      </Form.Item>
      <Form.Item name="sizeCodeId" className="mb-0 w-full">
        <Select options={sizeCodeOptions} placeholder={t('Storage room size')} bordered containerClassName="w-full" className="w-full" />
      </Form.Item>
      <Button type="submit" size="medium" isLoading={isLoading} containerClassName="w-full" className="w-full">
        {t('Book a tour')}
      </Button>
    </Form>
  );
});
